import { DataUtilities } from "../../rule-engine-core/Common/DataUtilities";
import { IConfigStorage } from "../../rule-engine-core/Common/IConfigStorage";
import { ValidationIssue } from "../../rule-engine-core/Common/IDesignValidationProvider";
import { ExpressionConfiguration } from "../../rule-engine-core/Expressions/ExpressionConfiguration";
import { FormConfiguration } from "../FormConfiguration";

export enum FormRuleType {
    VALIDATION = "Data Validation",
    CONTROL_ENABLED = "Control Enabled",
    CONTROL_VISIBILITY = "Control Visibility",
    CHANGE_VALUE = "Change Value",
    EXIT_FORM = "Exit Form",
    CHANGE_FOCUS = "Change Focus",
}

// todo: implement config storage and validation
// todo: unit test config storage and validation
// todo: unit test form config storage and validation

abstract class FormRuleData implements IConfigStorage {
    type: FormRuleType;

    constructor(type: FormRuleType) {
        this.type = type;
    }

    abstract getDesignValidationIssues(form: FormConfiguration): ValidationIssue[];

    getStorageData() {
        let obj = {};
        Reflect.set(obj, "type", this.type);

        return obj;
    }

    setStorageData(data: object) {
        this.type = Reflect.get(data, "type");
    }
}

export class FormRuleValidationData extends FormRuleData {
    validationMessage: string;
    controlId: string;

    constructor(validationMessage: string, controlId: string) {
        super(FormRuleType.VALIDATION);
        this.validationMessage = validationMessage;
        this.controlId = controlId;
    }

    getStorageData() {
        let obj = {};
        obj = super.getStorageData();
        Reflect.set(obj, "validationMessage", this.validationMessage);
        return obj;
    }

    setStorageData(data: object) {
        super.setStorageData(data);
        this.validationMessage = Reflect.get(data, "validationMessage");
    }

    getDesignValidationIssues(form: FormConfiguration): ValidationIssue[] {
        let issues: ValidationIssue[] = [];
        if (this.validationMessage === "") {
            issues.push(new ValidationIssue("Validation Message is missing!"));
        }
        if (this.controlId === "") {
            issues.push(new ValidationIssue("datapath is missing!"));
        }
        try {
            DataUtilities.getFieldFromDataDefinitionByName(form.getFormDatDefinition().fields, this.controlId);
        } catch (e: any) {
            if (e.message === `Can not find the field by path: ${this.controlId}`) {
                issues.push(new ValidationIssue("Datapath is not valid!"));
            }
        }
        return issues;
    }
}

export class FormRuleControlVisibilityData extends FormRuleData {
    controlId: string | string[];

    constructor(controlId: string | string[]) {
        super(FormRuleType.CONTROL_VISIBILITY);
        this.controlId = controlId;
    }

    getStorageData() {
        let obj = {};
        obj = super.getStorageData();
        Reflect.set(obj, "controlId", this.controlId);
        return obj;
    }

    setStorageData(data: object) {
        super.setStorageData(data);
        this.controlId = Reflect.get(data, "controlId");
    }

    getDesignValidationIssues(form: FormConfiguration): ValidationIssue[] {
        let issues: ValidationIssue[] = [];
        if (this.controlId === "") {
            issues.push(new ValidationIssue("Control Id is missing!"));
        }
        if (typeof this.controlId === "string" && form.find(this.controlId) === null) {
            issues.push(new ValidationIssue("Control Id is not valid!"));
        }
        if (typeof this.controlId === "object") {
            for (const controlId of this.controlId) {
                if (form.find(controlId) === null) {
                    issues.push(new ValidationIssue(`Control Id ${controlId} is not valid!`));
                }
            }
        }
        return issues;
    }
}

export class FormRuleControlEnabledData extends FormRuleData {
    controlId: string | string[];

    constructor(controlId: string | string[]) {
        super(FormRuleType.CONTROL_ENABLED);
        this.controlId = controlId;
    }

    getStorageData() {
        let obj = {};
        obj = super.getStorageData();
        Reflect.set(obj, "controlId", this.controlId);
        return obj;
    }

    setStorageData(data: object) {
        super.setStorageData(data);
        this.controlId = Reflect.get(data, "controlId");
    }

    getDesignValidationIssues(form: FormConfiguration): ValidationIssue[] {
        let issues: ValidationIssue[] = [];
        if (this.controlId === "") {
            issues.push(new ValidationIssue("Control Id is missing!"));
        }
        if (typeof this.controlId === "string" && form.find(this.controlId) === null) {
            issues.push(new ValidationIssue("Control Id is not valid!"));
        }
        if (typeof this.controlId === "object") {
            for (const controlId of this.controlId) {
                if (form.find(controlId) === null) {
                    issues.push(new ValidationIssue(`Control Id ${controlId} is not valid!`));
                }
            }
        }
        return issues;
    }
}

export class FormRuleChangeFocusData extends FormRuleData {
    controlId: string | string[];

    constructor(controlId: string | string[]) {
        super(FormRuleType.CHANGE_FOCUS);
        this.controlId = controlId;
    }

    getStorageData() {
        let obj = {};
        obj = super.getStorageData();
        Reflect.set(obj, "controlId", this.controlId);
        return obj;
    }

    setStorageData(data: object) {
        super.setStorageData(data);
        this.controlId = Reflect.get(data, "controlId");
    }

    getDesignValidationIssues(form: FormConfiguration): ValidationIssue[] {
        let issues: ValidationIssue[] = [];
        if (this.controlId === "") {
            issues.push(new ValidationIssue("Control Id is missing!"));
        }
        if (typeof this.controlId === "string" && form.find(this.controlId) === null) {
            issues.push(new ValidationIssue("Control Id is not valid!"));
        }
        if (typeof this.controlId === "object") {
            for (const controlId of this.controlId) {
                if (form.find(controlId) === null) {
                    issues.push(new ValidationIssue(`Control Id ${controlId} is not valid!`));
                }
            }
        }
        return issues;
    }
}

export class FormRuleChangeValueData extends FormRuleData {
    dataPath: string;
    value: ExpressionConfiguration;

    constructor(dataPath: string, value: ExpressionConfiguration) {
        super(FormRuleType.CHANGE_VALUE);
        this.dataPath = dataPath;
        this.value = value;
    }

    getStorageData() {
        let obj = {};
        obj = super.getStorageData();
        Reflect.set(obj, "dataPath", this.dataPath);
        Reflect.set(obj, "value", this.value.getStorageData());
        return obj;
    }

    setStorageData(data: object) {
        super.setStorageData(data);
        this.dataPath = Reflect.get(data, "dataPath");
        this.value.setStorageData(Reflect.get(data, "value"));
    }

    // eslint-disable-next-line
    getDesignValidationIssues(form: FormConfiguration): ValidationIssue[] {
        let issues: ValidationIssue[] = [];
        if (this.dataPath === "") {
            issues.push(new ValidationIssue("Data Path is missing!"));
        }

        // try {
        //     DataUtilities.getFieldFromDataDefinitionByProperty(form.getFormDatDefinition().fields, this.dataPath, "name");
        // } catch (e: any) {
        //     if (e.message === `Can not find the field by path: ${this.dataPath}`) {
        //         issues.push(new ValidationIssue("Value Change Path is not valid!"));
        //     }
        // }

        return [...issues, ...this.value.expressionRoot.getValidationIssues()];
    }
}

export class FormRuleExitFormData extends FormRuleData {
    exitOnPath: string;

    constructor(exitOnPath: string) {
        super(FormRuleType.EXIT_FORM);
        this.exitOnPath = exitOnPath;
    }

    getStorageData() {
        let obj = {};
        obj = super.getStorageData();
        Reflect.set(obj, "exitOnPath", this.exitOnPath);
        return obj;
    }

    setStorageData(data: object) {
        super.setStorageData(data);
        this.exitOnPath = Reflect.get(data, "exitOnPath");
    }

    // eslint-disable-next-line
    getDesignValidationIssues(form: FormConfiguration): ValidationIssue[] {
        let issues: ValidationIssue[] = [];
        if (this.exitOnPath === "") {
            issues.push(new ValidationIssue("Exit on Path is missing!"));
        }

        // try {
        //     DataUtilities.getFieldFromDataDefinitionByProperty(form.getFormDatDefinition().fields, this.exitOnPath, "name");
        // } catch (e: any) {
        //     if (e.message === `Can not find the field by path: ${this.exitOnPath}`) {
        //         issues.push(new ValidationIssue("Exit on Path is not valid!"));
        //     }
        // }
        return issues;
    }
}

export class FormRule implements IConfigStorage {
    enabled: boolean = true;
    expression: ExpressionConfiguration;
    // if empty, will respond to all
    watchingValues: string[];
    watchingValuesMode: string;
    data?: FormRuleData;

    constructor(
        expression: ExpressionConfiguration,
        watchingValues: string[] = [],
        watchingValuesMode: string = "value used in rule",
    ) {
        this.expression = expression;
        this.watchingValues = watchingValues;
        this.watchingValuesMode = watchingValuesMode;

        this.data = new FormRuleValidationData("", "");
    }

    private _ruleType: FormRuleType = FormRuleType.VALIDATION;

    get ruleType() {
        return this._ruleType;
    }

    set ruleType(type: FormRuleType) {
        switch (type) {
            case FormRuleType.VALIDATION:
                this.data = new FormRuleValidationData("", "");
                break;
            case FormRuleType.CHANGE_FOCUS:
                this.data = new FormRuleChangeFocusData("");
                break;
            case FormRuleType.CHANGE_VALUE:
                let value = new ExpressionConfiguration();
                value.inputData = () => {
                    return this.expression.getInputDefinition();
                };
                this.data = new FormRuleChangeValueData("", value);
                break;
            case FormRuleType.EXIT_FORM:
                this.data = new FormRuleExitFormData("");
                break;
            case FormRuleType.CONTROL_ENABLED:
                this.data = new FormRuleControlEnabledData("");
                break;
            case FormRuleType.CONTROL_VISIBILITY:
                this.data = new FormRuleControlVisibilityData("");
                break;
        }
        this._ruleType = type;
    }

    getStorageData() {
        let obj = {};

        Reflect.set(obj, "enabled", this.enabled);
        Reflect.set(obj, "ruleType", this.ruleType);
        Reflect.set(obj, "expression", this.expression.getStorageData());
        Reflect.set(obj, "watchingValues", this.watchingValues);
        Reflect.set(obj, "watchingValuesMode", this.watchingValuesMode);

        Reflect.set(obj, "data", this.data?.getStorageData());

        return obj;
    }

    setStorageData(data: object) {
        this.enabled = Reflect.get(data, "enabled");
        this.ruleType = Reflect.get(data, "ruleType");
        this.expression = new ExpressionConfiguration();
        this.expression.setStorageData(Reflect.get(data, "expression"));
        this.watchingValues = Reflect.get(data, "watchingValues");
        this.watchingValuesMode = Reflect.get(data, "watchingValuesMode");
        this.data?.setStorageData(Reflect.get(data, "data"));
    }

    getDesignValidationIssues(form: FormConfiguration): ValidationIssue[] {
        let issues = this.data?.getDesignValidationIssues(form) || [];

        if (!this.data) {
            issues.push(new ValidationIssue("Data is missing"));
        }

        if (!this.expression) {
            issues.push(new ValidationIssue("Expression is missing"));
        } else {
            issues.concat(this.expression.getDesignValidationIssues());
        }
        return issues;
    }
}
